import { AdjustmentsVerticalIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import {
  useDeleteProfessionalMutation, useGetFilteredProfessionalsQuery,
  useLazyGetAllProfessionalsQuery,
  useLazyGetFilteredProfessionalsQuery
} from "../../../../store/admin/professionalUserInfo";
import DashboardAdminCalendar from "../../../calendar/DashboardAdminCalendar";
import ProfessionalsTable from "./tables/ProfessionalsTable";

import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetAdminMetricsQuery,
  useLazyGetAdminMetricsQuery
} from "../../../../store/admin/adminMetrics";
import { useGetAllUserStatusesQuery } from "../../../../store/admin/userStatuses";
import { formatDate } from "../../../../utilities/utils";
import DeleteSmsModal from "../../../modal/DeleteSmsModal";
import {
  calendarData, comparedYearData
} from "../adminData";
import DropDownFilters from "../dashboardAdminUser/DropDownFilters";
import GreenChartSvg from "../svgIcons/GreenChartSvg";
import RedChartSvg from "../svgIcons/RedChartSvg";
import NumberSkeleton from "../../../commons/Skeleton/NumberSkeleton";

const ProfessionalDataPage = () => {
  const dropdownRef = useRef(null);
  const calendarRef = useRef(null);
  const navigate = useNavigate();
  const [metricsData, setMetricsData] = useState({});
  const [filterActive, setFilterActive] = useState(false);
  const [allProfessionalsData, setAllProfessionalData] = useState();
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLead, setDeleteLead] = useState(null);
  const currentDate = moment();
  const startOfMonth = currentDate.startOf("month");
  const [selectRange, setSelectRange] = useState({
    startDate:  moment().format("MM/DD/YYYY"),
    endDate: moment().format("MM/DD/YYYY"),
  });
  const [selectedCalendarFilter, setSelectedCalendarFilter] =
    useState("Today");
  const [selectedCompareFilter, setSelectedCompareFilter] = useState([
    "Previous period",
  ]);

  const formattedStartDate = moment(
    selectRange?.startDate,
    "MM/DD/YYYY"
  ).format("YYYY-MM-DD");
  const formattedEndDate = moment(selectRange?.endDate, "MM/DD/YYYY").format(
    "YYYY-MM-DD"
  );

  const {
    data: allProfessionalsInfo,
    isLoading: allProfessionalsInfoLoader,
    isSuccess: allProfessionalsInfoSuccess,
  } = useGetFilteredProfessionalsQuery({
    startDate: startOfMonth.format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });

  const [
    refetch,
    { data, isLoading: allProfessionalsRefetchLoader, isFetching },
  ] = useLazyGetAllProfessionalsQuery();

  const { data: allUserStatuses } = useGetAllUserStatusesQuery();

  const [
    getAllFilteredProfessionals,
    {
      data: filteredProfessionals,
      isLoading: filteredProfessionalsLoader,
      isSuccess: filteredProfessionalsSuccess,
      isFetching: filteredProfessionalsFetching,
    },
  ] = useLazyGetFilteredProfessionalsQuery();

  const {
    data: allAdminMetricsData,
    isSuccess: allAdminMetricsDataSuccess,
    isLoading: allAdminMetricsDataLoader,
  } = useGetAdminMetricsQuery({
    startDate: startOfMonth.format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });

  const [
    getAllAdminMetricsData,
    {
      data: adminMetricsData,
      isLoading: adminMetricsDataLoader,
      isSuccess: adminMetricsDataSuccess,
      isFetching: adminMetricsDataFetching,
    },
  ] = useLazyGetAdminMetricsQuery();

  const [
    deleteProfessional,
    {
      data: deleteProfessionalData,
      isSuccess: deleteProfessionalSuccess,
      isLoading: deleteProfessionalLoader,
      isError: deleteProfessionalIsError,
      error: deleteProfessionalError,
    },
  ] = useDeleteProfessionalMutation();

  const handleOutsideClick = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setFilterActive(false);
    }
    if (calendarRef.current && !calendarRef.current.contains(event.target)) {
      setCalendarOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!deleteProfessionalLoader) {
      if (deleteProfessionalSuccess) {
        toast.success(deleteProfessionalData.message);
        applyFiltered();
      } else if (deleteProfessionalIsError && deleteProfessionalError) {
        toast.error("Something went wrong, please try again");
      }
      setShowDeleteModal(false);
      setDeleteLead(null);
    }
  }, [deleteProfessionalSuccess, deleteProfessionalIsError]);

  useEffect(() => {
    if (adminMetricsDataSuccess && !adminMetricsDataLoader) {
      setMetricsData(adminMetricsData?.data);
      setCalendarOpen(false);
    }
  }, [adminMetricsDataSuccess, adminMetricsDataFetching]);

  useEffect(() => {
    if (allAdminMetricsDataSuccess && !allAdminMetricsDataLoader) {
      setMetricsData(allAdminMetricsData?.data);
    }
  }, [allAdminMetricsDataSuccess]);

  useEffect(() => {
    if (filteredProfessionalsSuccess && !filteredProfessionalsLoader) {
      setCalendarOpen(false);
      setFilterActive(false);
    }
  }, [filteredProfessionalsSuccess, filteredProfessionalsFetching]);

  useEffect(() => {
    if (filteredProfessionals) {
      setAllProfessionalData(filteredProfessionals);
    }
  }, [filteredProfessionals]);

  useEffect(() => {
    if (allProfessionalsInfo) {
      setAllProfessionalData(allProfessionalsInfo);
    }
  }, [allProfessionalsInfo]);

  useEffect(() => {
    if (data) {
      setAllProfessionalData(data);
    }
  }, [data]);

  useEffect(() => {
    if (isFetching) {
      setAllProfessionalData({});
    }
  }, [isFetching]);

  const calendarFilterChange = (value) => {
    setSelectedCalendarFilter(value);

    const selectedPeriod =
      calendarData && calendarData?.find((item) => item.text === value)?.period;

    if (selectedPeriod) {
      setSelectRange({
        startDate: selectedPeriod.start,
        endDate: selectedPeriod.end,
      });
    } else {
      setSelectRange({});
    }
  };

  const compareFilterChange = (value) => {
    setSelectedCompareFilter(value);
  };

  const handleProfessionalFilter = () => {
    setFilterActive((prevState) => !prevState);
  };

  const applyFiltered = () => {
    const formattedStartDate = moment(
      selectRange.startDate,
      "MM/DD/YYYY"
    ).format("YYYY-MM-DD");
    const formattedEndDate = moment(selectRange.endDate, "MM/DD/YYYY").format(
      "YYYY-MM-DD"
    );
    if (selectedCalendarFilter === "All") {
      getAllFilteredProfessionals({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        status_ids: selectedStatus.map((status) => status.id).join(","),
      });
    } else {
      getAllFilteredProfessionals({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        status_ids: selectedStatus.map((status) => status.id).join(","),
      });
    }
  };

  const handleCalendarChange = (value, event) => {
    const startDate = formatDate(value[0]);
    const endDate = formatDate(value[1]);
    setSelectRange({ startDate: startDate, endDate: endDate });
    setSelectedCalendarFilter("Custom date");
  };

  const handleDateFilter = () => {
    const formattedStartDate = moment(
      selectRange.startDate,
      "MM/DD/YYYY"
    ).format("YYYY-MM-DD");
    const formattedEndDate = moment(selectRange.endDate, "MM/DD/YYYY").format(
      "YYYY-MM-DD"
    );

    if (selectedCalendarFilter === "All") {
      getAllAdminMetricsData({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    } else {
      getAllAdminMetricsData({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    }

    if (selectedCalendarFilter === "All") {
      getAllFilteredProfessionals({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        status_ids: selectedStatus.map((status) => status.id).join(","),
      });
    } else {
      getAllFilteredProfessionals({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        status_ids: selectedStatus.map((status) => status.id).join(","),
      });
    }
  };

  const handleDropDownMenu = (eventType, data) => {
    switch (eventType) {
      case "delete":
        setShowDeleteModal(true);
        setDeleteLead(data);
        break;
      case "edit":
        navigate("/admin-dashboard/professional-profile", {
          state: {
            professionalId: data?.id,
          },
        });
        break;
    }
  };

  const handleOpen = () => {
    setCalendarOpen((prevState) => !prevState);
  };

  const handleClose = () => {
    setCalendarOpen(false);
  };

  return (
    <>
      <div className="col-span-12">
        <div className="flex justify-between xl:items-center flex-col xl:flex-row gap-5 mb-[14px] xl:mb-5">
          <h3 className="text-xl leading-[160%] font-bold text-[#04091E]">
            Professional Data
          </h3>
          <div ref={calendarRef}>
            <DashboardAdminCalendar
              calendarFilter={calendarData}
              compareFilter={comparedYearData}
              selectedCalendarFilter={selectedCalendarFilter}
              calendarFilterChange={calendarFilterChange}
              selectedCompareFilter={selectedCompareFilter}
              compareFilterChange={compareFilterChange}
              handleCalendarChange={handleCalendarChange}
              setSelectRange={setSelectRange}
              selectRange={selectRange}
              handleDateFilter={handleDateFilter}
              calendarOpen={calendarOpen}
              setCalendarOpen={setCalendarOpen}
              handleOpen={handleOpen}
              handleClose={handleClose}
            />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-5 xxl:gap-6 mb-[30px]">
          <div className="bg-white border border-[#EBEFF3] p-6 col-span-12 sm:col-span-6 xxl:col-span-3 rounded-md">
            <span className="text-xs leading-[160%] text-[#485868] font-bold uppercase mb-1">
              Total Current
            </span>
            <h6 className="text-base font-bold leading-[160%] text-customGray mb-6 capitalize">
              Active Professionals
            </h6>
            <div className="flex justify-between items-end">
              <div>
                <span className="text-[32px] leading-[125%] font-black text-customGray">
                  {metricsData?.active_accounts ?? <NumberSkeleton/>}
                </span>
                <div className="flex items-center gap-1.5">
                  {metricsData?.active_accounts_change?.startsWith("-") ? (
                    <RedChartSvg />
                  ) : (
                    <GreenChartSvg />
                  )}
                  <span
                    className="text-sm font-bold leading-[180%]"
                    style={{
                      color: metricsData?.active_accounts_change?.startsWith(
                        "-"
                      )
                        ? "#EF2121"
                        : "#16CA7C",
                    }}
                  >
                    {metricsData?.active_accounts_change ?? "+0%"}
                  </span>
                </div>

                <p className="text-xs font-medium leading-[160%] text-[#485868]">
                  from previous period
                </p>
              </div>
              <div className="w-[57px] grid grid-cols-12 gap-1 items-end">
                <div className="col-span-6 h-6 bg-[#BEC2C5] rounded-sm"></div>
                <div className="col-span-6 h-16 bg-primary rounded-sm"></div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEFF3] p-6 col-span-12 sm:col-span-6 xxl:col-span-3 rounded-md">
            <span className="text-xs leading-[160%] text-[#485868] font-bold uppercase mb-1">
              Total Current
            </span>
            <h6 className="text-base font-bold leading-[160%] text-customGray mb-6 capitalize">
              Past Due Accounts
            </h6>
            <div className="flex justify-between items-end">
              <div>
                <span className="text-[32px] leading-[125%] font-black text-customGray">
                  {metricsData?.past_due_accounts ?? <NumberSkeleton/>}
                </span>
                <div className="flex items-center gap-1.5">
                  {metricsData?.past_due_accounts_change?.startsWith("-") ? (
                    <RedChartSvg />
                  ) : (
                    <GreenChartSvg />
                  )}
                  <span
                    className="text-sm font-bold leading-[180%]"
                    style={{
                      color: metricsData?.past_due_accounts_change?.startsWith(
                        "-"
                      )
                        ? "#EF2121"
                        : "#16CA7C",
                    }}
                  >
                    {metricsData?.past_due_accounts_change ?? "+0%"}
                  </span>
                </div>

                <p className="text-xs font-medium leading-[160%] text-[#485868]">
                  from previous period
                </p>
              </div>
              <div className="w-[57px] grid grid-cols-12 gap-1 items-end">
                <div className="col-span-6 h-6 bg-[#BEC2C5] rounded-sm"></div>
                <div className="col-span-6 h-16 bg-primary rounded-sm"></div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEFF3] p-6 col-span-12 sm:col-span-6 xxl:col-span-3 rounded-md">
            <span className="text-xs leading-[160%] text-[#485868] font-bold uppercase mb-1">
              Total Current
            </span>
            <h6 className="text-base font-bold leading-[160%] text-customGray mb-6 capitalize">
              Cancelled Accounts
            </h6>
            <div className="flex justify-between items-end">
              <div>
                <span className="text-[32px] leading-[125%] font-black text-customGray">
                  {metricsData?.cancelled_accounts ?? <NumberSkeleton/>}
                </span>
                <div className="flex items-center gap-1.5">
                  {metricsData?.cancelled_accounts_change?.startsWith("-") ? (
                    <RedChartSvg />
                  ) : (
                    <GreenChartSvg />
                  )}
                  <span
                    className="text-sm font-bold leading-[180%]"
                    style={{
                      color: metricsData?.cancelled_accounts_change?.startsWith(
                        "-"
                      )
                        ? "#EF2121"
                        : "#16CA7C",
                    }}
                  >
                    {metricsData?.cancelled_accounts_change ?? "+0%"}
                  </span>
                </div>

                <p className="text-xs font-medium leading-[160%] text-[#485868]">
                  from previous period
                </p>
              </div>
              <div className="w-[57px] grid grid-cols-12 gap-1 items-end">
                <div className="col-span-6 h-6 bg-[#BEC2C5] rounded-sm"></div>
                <div className="col-span-6 h-16 bg-primary rounded-sm"></div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEFF3] p-6 col-span-12 sm:col-span-6 xxl:col-span-3 rounded-md">
            <span className="text-xs leading-[160%] text-[#485868] font-bold uppercase mb-1">
              Total Current
            </span>
            <h6 className="text-base font-bold leading-[160%] text-customGray mb-6 capitalize">
              Delinquent Accounts
            </h6>
            <div className="flex justify-between items-end">
              <div>
                <span className="text-[32px] leading-[125%] font-black text-customGray">
                  {metricsData?.delinquent_accounts ?? <NumberSkeleton/>}
                </span>
                <div className="flex items-center gap-1.5">
                  {metricsData?.delinquent_accounts_change?.startsWith("-") ? (
                    <RedChartSvg />
                  ) : (
                    <GreenChartSvg />
                  )}
                  <span
                    className="text-sm font-bold leading-[180%]"
                    style={{
                      color:
                        metricsData?.delinquent_accounts_change?.startsWith("-")
                          ? "#EF2121"
                          : "#16CA7C",
                    }}
                  >
                    {metricsData?.delinquent_accounts_change ?? "+0%"}
                  </span>
                </div>

                <p className="text-xs font-medium leading-[160%] text-[#485868]">
                  from previous period
                </p>
              </div>
              <div className="w-[57px] grid grid-cols-12 gap-1 items-end">
                <div className="col-span-6 h-6 bg-[#BEC2C5] rounded-sm"></div>
                <div className="col-span-6 h-16 bg-primary rounded-sm"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-start md:items-center gap-5 mb-[14px]">
          <h3 className="text-xl leading-[160%] font-bold text-[#04091E] capitalize">
            HelpMatch professionals
          </h3>
          <div className="relative">
            <button
              className={`w-12 h-12 flex items-center justify-center gap-2.5 md:gap-0 rounded-[10px] col-span-6 transition-all ${
                filterActive
                  ? "bg-[#D2ECFF] text-primary"
                  : "bg-[#EBEFF3] text-[#485868]"
              }`}
              onClick={handleProfessionalFilter}
            >
              <AdjustmentsVerticalIcon className="w-6 h-6" />
            </button>
            <div
              ref={dropdownRef}
              className={`notificationDropdown absolute right-0 top-[55px] md:top-[60px] z-[380] opacity-0 ${
                filterActive ? "notificationOpen" : ""
              }`}
            >
              <DropDownFilters
                setFilterActive={setFilterActive}
                filterActive={filterActive}
                statusData={allUserStatuses}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                applyFiltered={applyFiltered}
                loader={filteredProfessionalsLoader}
                setCreditRenewal={false}
              />
            </div>
          </div>
        </div>
        <ProfessionalsTable
          professionalData={allProfessionalsData}
          getUpdateData={(page, perPage) => {
            getAllFilteredProfessionals({
              page,
              perPage,
              startDate: formattedStartDate,
              endDate: formattedEndDate,
              status_ids: selectedStatus.map((status) => status.id).join(","),
            });
          }}
          handleDropDownMenu={handleDropDownMenu}
        />
      </div>
      <DeleteSmsModal
        isOpen={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDelete={() => deleteProfessional(deleteLead.id)}
        deleteLoader={deleteProfessionalLoader}
        text="Do you really want to delete this Professional?"
      />
    </>
  );
};

export default ProfessionalDataPage;
